import {
  AfterViewInit,
  Component,
  inject,
  input,
  OnInit,
  output,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationDto } from '@bannerflow/types.notification-service';
import { UIButtonComponent, UIPopoverTargetDirective } from '@bannerflow/ui';
import { FollowStateChanged } from '../../domain/followstate.notifications.types';
import { NotificationClickEvent } from '../../notification.types';
import { NotificationService } from '../../services/notification.service';
import { SignalRService } from '../../services/signalr.service';
import { NotificationPopoverComponent } from './notification-popover/notification-popover.component';
import { QuickPopoverComponent } from './quick-popover/quick-popover.component';

@Component({
  imports: [
    NotificationPopoverComponent,
    QuickPopoverComponent,
    UIButtonComponent,
    UIPopoverTargetDirective
  ],
  selector: 'ns-notifications-component',
  templateUrl: './notifications.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './notifications.component.scss'
})
export class NSNotificationsComponent implements OnInit, AfterViewInit {
  private notificationsService = inject(NotificationService);
  private signalRService = inject(SignalRService);

  private notificationPopoverTarget =
    viewChild.required<UIPopoverTargetDirective>('notificationPopoverTarget');
  private notificationPopover =
    viewChild.required<NotificationPopoverComponent>('notificationPopover');
  private quickPopover =
    viewChild.required<QuickPopoverComponent>('quickPopover');

  disabled = input<boolean>(false);

  newNotificationReceived = output<NotificationDto>();
  creativeSetFollowStateChanged = output<FollowStateChanged>();
  notificationClicked = output<NotificationClickEvent>();

  hasUnreadNotifications = this.notificationsService.hasUnreadNotifications;

  constructor() {
    this.signalRService.newNotification$
      .pipe(takeUntilDestroyed())
      .subscribe(newNotification => {
        if (newNotification) {
          this.notificationsService.loadNumberOfUnreadNotifications();
          this.notificationsService.loadAndPrependNotification(newNotification);
        }
      });

    this.signalRService.followStateChanged$
      .pipe(takeUntilDestroyed())
      .subscribe(followStateChanged => {
        if (followStateChanged) {
          this.creativeSetFollowStateChanged.emit(followStateChanged);
        }
      });

    this.notificationsService.newNotification$
      .pipe(takeUntilDestroyed())
      .subscribe(newNotification => {
        if (newNotification) {
          this.showQuickPopover();
          this.newNotificationReceived.emit(newNotification);
        }
      });
  }

  ngOnInit(): void {
    this.notificationsService.loadNumberOfUnreadNotifications();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.signalRService.startConnection();
  }

  bellClicked(): void {
    if (this.disabled()) {
      return;
    }
    const notificationPopover = this.notificationPopover();
    if (!notificationPopover) {
      return;
    }
    const target = this.notificationPopoverTarget();
    if (!target) {
      return;
    }
    notificationPopover.openPopover(target);
  }

  private showQuickPopover(): void {
    if (this.disabled()) {
      return;
    }
    const quickPopover = this.quickPopover();
    if (!quickPopover) {
      return;
    }
    const target = this.notificationPopoverTarget();
    if (!target) {
      return;
    }
    quickPopover.openPopover(target);
  }
}
